// src/components/AppEnhancements.jsx
// Contains: ProgressJourneyBar + OnboardingTooltip system
import React, { useState, useEffect } from 'react';
import { userKey } from '../utils/storageHelper.js';
import { supabase } from '../lib/supabaseClient.js';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { AIDecisionsTab } from './AIDecisionsTab';
import QRCode from 'qrcode';
import { EUFlagIcon } from './EUFlagIcon.jsx';

const TOOLTIP_KEY = 'qais_tooltips_dismissed';

const JOURNEY_STEPS = [
  { key: 'dashboard',    icon: '👤', label: 'Profile',     check: () => !!localStorage.getItem(userKey('qais_onboarding')) },
  { key: 'risk',         icon: '⚖️', label: 'Risk Score',  check: () => !!localStorage.getItem(userKey('qais_risk_result')) },
{ key: 'euclassifier', icon: <EUFlagIcon size={20} />, label: 'EU Classify', check: () =>
      !!localStorage.getItem(userKey('qais_eu_classifier_result')) ||
      !!localStorage.getItem(userKey('qais_eu_classifier_skipped')) ||
      !!localStorage.getItem(userKey('qais_eu_advisory_result'))
  },
  { key: 'checklist',    icon: '✏️', label: 'Checklist',   check: () => !!localStorage.getItem(userKey('qais_checklist_setup')) },
  { key: 'documents',    icon: '📄', label: 'Documents',   check: () => parseInt(localStorage.getItem(userKey('qais_docs_generated')) || '0') > 0 },
];

const TOOLTIPS = {
  dashboard:    { title: '👋 Start here!', text: 'Complete your profile in the Dashboard first. Your answers personalize every other tool.' },
  risk:         { title: '⚖️ Assess your risk', text: 'Answer 12 quick questions for multi-framework AI risk classification (EU AI Act / NIST AI RMF / ISO 42001) and a personalized action plan.' },
  euclassifier: { title: '🇪🇺 Classify your AI', text: 'Describe your AI system in plain English and get an instant EU AI Act risk classification with specific obligations.' },
  checklist:    { title: '✏️ Build your checklist', text: 'Generate a personalized compliance to-do list based on your risk level across all 3 frameworks.' },
  documents:    { title: '📄 Generate documents', text: 'Create AI compliance documents (policies, assessments, data plans) tailored to your organization.' },
  mapper:       { title: '🔗 Map frameworks', text: 'Generate a unified compliance document that satisfies ISO 42001, EU AI Act, and NIST AI RMF simultaneously.' },
  chatbot:      { title: '🤖 Ask anything', text: 'Chat with your AI compliance assistant. Full conversation history is saved automatically.' },
};

export function ProgressJourneyBar({ currentPage, user, onNavigate }) {
  const [completed, setCompleted] = useState({});
  const [ready, setReady] = useState(false);
  const [showAIDecisionsModal, setShowAIDecisionsModal] = useState(false);
  const [blockchainSaved, setBlockchainSaved] = useState(() => {
    try { return localStorage.getItem(userKey('qais_blockchain_saved')) === 'true'; } catch { return false; }
  });
  const [saving, setSaving] = useState(false);
  const [ledgerRecord, setLedgerRecord] = useState(() => {
    try { return JSON.parse(localStorage.getItem(userKey('qais_ledger_record')) || 'null'); } catch { return null; }
  });
  // Task #7: opt-in checkbox -- controls whether the sealed record's summary
  // (company/system/framework/status/risk) is shown on the public verify page.
  // Fails closed by default (unchecked) -- matches backend's public_disclosure
  // handling, which treats anything other than an explicit true as hidden.
  const [publicDisclosure, setPublicDisclosure] = useState(false);

  // ── Pre‑fetch AI decisions (makes modal instant) ──
  const [prefetchedDecisions, setPrefetchedDecisions] = useState(null);
  const [prefetchedLoading, setPrefetchedLoading] = useState(false);

  // Task #9: reconcile "sealed" state with real server truth on load.
  // Previously this component trusted localStorage (qais_blockchain_saved)
  // alone, which went stale across devices/browsers and after the dev-only
  // "Reset Test Mode" button (which only ever cleared local state -- the
  // server-side qledger_sealed flag was never affected by it in the first
  // place, since that flag is set by finalizeSeal() on the backend).
  // Fails open on infra errors -- never wrongly flip a real seal to unsealed
  // just because the status check itself failed to load.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        if (!token) return;

        const res = await fetch('/api/phase2/qledger-status', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return; // fail open -- infra error, don't touch existing state
        const data = await res.json();
        if (!data.success || cancelled) return;

        if (data.qledger_sealed === true) {
          setBlockchainSaved(true);
          try { localStorage.setItem(userKey('qais_blockchain_saved'), 'true'); } catch {}
        } else {
          // Server is authoritative and says NOT sealed -- correct any
          // stale local-only "sealed" state (e.g. left over from a
          // different device/browser, or a manual localStorage edit).
          setBlockchainSaved(false);
          setLedgerRecord(null);
          try {
            localStorage.removeItem(userKey('qais_blockchain_saved'));
            localStorage.removeItem(userKey('qais_ledger_record'));
          } catch {}
        }
      } catch (err) {
        console.error('[qledger-status] check failed:', err.message);
        // fail open -- never block/mislead the user over an infra hiccup
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id]);

// Pre‑fetch AI decisions when user is logged in (makes modal instant)
useEffect(() => {
  // Only run if we have a user ID
  if (!user?.id) return;

  const fetchEarly = async () => {
    setPrefetchedLoading(true);
    try {
      // Get fresh session token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('No active session token');

      const params = new URLSearchParams();
      params.append('user_id', user.id);
      const url = `/api/ai-decisions?${params.toString()}`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPrefetchedDecisions(data);
    } catch (err) {
      console.error('Pre‑fetch error:', err);
      // Don't show error to user – just suppress
    } finally {
      setPrefetchedLoading(false);
    }
  };

  fetchEarly();
}, [user?.id]); // ← re‑run when user ID changes


// ============================================================
// PDF Certificate of Compliance — A4 single-page with QR verify
// ============================================================
//
// Replaces the existing downloadCertificateAsPDF function in AppEnhancements.jsx.
//
// Design choices applied:
//   • Border:     Cyan gradient frame (Sample 4 — modern luxe)
//   • Background: Subtle watermark (logo behind text at ~4% opacity)
//   • QR code:    Bottom-right, links to LIVE /api/verify/:id endpoint
//   • Fields:     ALL 15 metadata fields packed onto a single A4 page
//   • Auditor:    No human signature (automated cert with cryptographic attestation)
// ============================================================
// PDF Certificate of Compliance v4 — A4 single-page with QR verify
// ============================================================
//
// Fixes from v3:
//   1. QR code now uses INLINE SVG (not <img>) — renders reliably in html2canvas
//   2. Verify URL no longer breaks awkwardly (uses overflow-wrap with hyphens disabled)
//   3. Empty space filled with expanded sections
//   4. Watermark logo bumped to 0.09 opacity AND section backgrounds made semi-transparent
//   5. Cryptographic Attestation expanded with quantum-safety context

const downloadCertificateAsPDF = async () => {
  if (!ledgerRecord) {
    alert('No sealed record to download.');
    return;
  }

  // ── Tier styling ──
  const tierStyling = {
    COMPLIANT:     { label: 'Audit-Ready Attested',  icon: '✅', color: '#15803d', bg: '#dcfce7', borderColor: '#22c55e' },
    PARTIAL:       { label: 'Partial Compliance',   icon: '🟡', color: '#a16207', bg: '#fef3c7', borderColor: '#ca8a04' },
    BASELINE:      { label: 'Baseline Assessment',  icon: '🟠', color: '#c2410c', bg: '#ffedd5', borderColor: '#ea580c' },
    NON_COMPLIANT: { label: 'Initial Assessment',   icon: '🔴', color: '#b91c1c', bg: '#fee2e2', borderColor: '#dc2626' },
  };
  const tier = tierStyling[ledgerRecord.status] || tierStyling.COMPLIANT;

  // ── Generate QR as INLINE SVG (renders reliably in html2canvas) ──
  const verifyUrl = `https://api.quantumaishield.com/api/verify/${ledgerRecord.certificate_id}`;
  let qrSvg = '';
  try {
    qrSvg = await QRCode.toString(verifyUrl, {
      type: 'svg',
      margin: 1,
      color: { dark: '#0e7c7c', light: '#ffffff' },
      errorCorrectionLevel: 'M',
      width: 200,
    });
    // Strip the SVG's outer width/height so we can control sizing via CSS
    qrSvg = qrSvg.replace(/width="\d+"\s*height="\d+"/i, 'width="100%" height="100%"');
  } catch (e) {
    console.warn('QR code generation failed:', e);
  }

  // ── Load logo ──
  let logoDataUrl = '';
  try {
    const resp = await fetch('/logo.png');
    const blob = await resp.blob();
    logoDataUrl = await new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Logo load failed:', e);
  }

  // ── Date helpers ──
  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const sealedDate    = fmtDate(ledgerRecord.sealed_at);
  const nextAuditDate = fmtDate(ledgerRecord.next_audit_date);
  const issuedDate    = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  // ── A4 portrait container ──
  const A4_WIDTH_PX  = 794;
  const A4_HEIGHT_PX = 1123;

  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed; top: -10000px; left: -10000px;
    width: ${A4_WIDTH_PX}px; height: ${A4_HEIGHT_PX}px;
    background: white;
    font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
    overflow: hidden;
  `;

  // ── 15 fields ──
  const reqsPassed = ledgerRecord.requirements_passed ?? 0;
  const reqsTotal  = ledgerRecord.requirements_total  ?? 0;
  const fields = [
    { label: 'Company',           value: ledgerRecord.company || '—' },
    { label: 'AI System',         value: ledgerRecord.ai_system || '—' },
    { label: 'Framework',         value: ledgerRecord.framework || '—' },
    { label: 'Status',            value: tier.label },
    { label: 'Risk Score',        value: `${ledgerRecord.score ?? 0}/100` },
    { label: 'Risk Level',        value: ledgerRecord.risk_level || '—' },
    { label: 'Requirements',      value: `${reqsPassed} / ${reqsTotal}` },
    { label: 'Sector',            value: ledgerRecord.sector || '—' },
    { label: 'Attestation ID',    value: ledgerRecord.certificate_id || '—' },
    { label: 'Block #',           value: ledgerRecord.block_index != null ? `#${ledgerRecord.block_index}` : '—' },
    { label: 'Sealed At',         value: sealedDate },
    { label: 'Next Audit Due',    value: nextAuditDate },
    { label: 'Quantum Algorithm', value: `${ledgerRecord.quantum_security?.signature_algorithm || 'ML-DSA-65'} (${ledgerRecord.quantum_security?.signature_standard || 'NIST FIPS 204'})` },
    { label: 'Document Hash',     value: ledgerRecord.document_hash ? ledgerRecord.document_hash.slice(0, 28) + '…' : '—' },
    { label: 'Prepared By',       value: ledgerRecord.prepared_by || ledgerRecord.company || '—' },
  ];

  container.innerHTML = `
    <div style="
      position: relative;
      width: 100%; height: 100%;
      box-sizing: border-box;
      padding: 28px;
      background: white;
    ">
      <!-- Cyan gradient frame -->
      <div style="
        position: absolute;
        inset: 14px;
        border-radius: 12px;
        padding: 4px;
        background: linear-gradient(135deg, #0e7c7c 0%, #06b6d4 25%, #67e8f9 50%, #06b6d4 75%, #0e7c7c 100%);
        z-index: 0;
      ">
        <div style="
          width: 100%; height: 100%;
          background: white;
          border-radius: 8px;
        "></div>
      </div>

      <!-- Watermark logo (more visible at 0.09) -->
      ${logoDataUrl ? `
        <img src="${logoDataUrl}" style="
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 500px; height: auto;
          opacity: 0.09;
          pointer-events: none;
          z-index: 1;
        " />
      ` : ''}

      <!-- Trial watermark -- Task #7: only shown for records sealed during
           the 14-day trial, so trial users can't pass off a trial-period
           record as a paid, fully-provisioned attestation. -->
      ${ledgerRecord.sealed_during_trial ? `
        <div style="
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-28deg);
          font-size: 64px;
          font-weight: 900;
          letter-spacing: 0.15em;
          color: rgba(220, 38, 38, 0.16);
          pointer-events: none;
          z-index: 3;
          white-space: nowrap;
        ">TRIAL SPECIMEN</div>
      ` : ''}

      <!-- Content (over watermark) -->
      <div style="
        position: relative;
        z-index: 2;
        height: 100%;
        padding: 30px 42px 28px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
      ">

        <!-- HEADER -->
        <div style="display: flex; align-items: center; justify-content: center; gap: 14px; margin-bottom: 6px;">
          ${logoDataUrl ? `<img src="${logoDataUrl}" style="height: 38px; width: auto;" />` : ''}
          <div style="text-align: left;">
            <div style="font-size: 16px; font-weight: 800; color: #0e7c7c; letter-spacing: 0.05em;">QUANTUM AI SHIELD</div>
            <div style="font-size: 8.5px; color: #64748b; letter-spacing: 0.12em; text-transform: uppercase;">Quantum-Safe AI GRC Platform</div>
          </div>
        </div>

        <!-- TITLE -->
        <div style="text-align: center; margin-bottom: 6px;">
          <div style="font-size: 9px; color: #64748b; letter-spacing: 0.3em; text-transform: uppercase; margin-bottom: 1px;">—— Self-Reported Attestation ——</div>
          <h1 style="
            font-size: 24px;
            font-weight: 900;
            color: #0e7c7c;
            margin: 0;
            letter-spacing: 0.02em;
            line-height: 1.1;
         ">QUANTUM-SEALED COMPLIANCE ATTESTATION</h1>
          <div style="
            width: 90px;
            height: 2.5px;
            background: linear-gradient(90deg, transparent 0%, #0e7c7c 50%, transparent 100%);
            margin: 5px auto 0;
            border-radius: 2px;
          "></div>
        </div>

        <!-- ISSUED TO -->
        <div style="text-align: center; margin-bottom: 6px;">
          <div style="font-size: 11px; color: #64748b; letter-spacing: 0.2em; text-transform: uppercase; margin-bottom: 2px;">This attestation is issued to</div>
          <div style="
            font-size: 20px;
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 2px;
            line-height: 1.2;
          ">${ledgerRecord.company || '—'}</div>
          <div style="font-size: 11px; color: #475569;">
            for the AI system <strong style="color: #0e7c7c;">${ledgerRecord.ai_system || '—'}</strong>
          </div>
        </div>

        <!-- STATUS BADGE -->
        <div style="display: flex; justify-content: center; margin-bottom: 10px;">
          <div style="
            display: inline-flex;
            align-items: center;
            gap: 7px;
            padding: 6px 20px;
            background: ${tier.bg};
            border: 2px solid ${tier.borderColor};
            border-radius: 28px;
          ">
            <span style="font-size: 14px;">${tier.icon}</span>
            <span style="
              font-size: 12px;
              font-weight: 800;
              color: ${tier.color};
              letter-spacing: 0.05em;
              text-transform: uppercase;
            ">${tier.label}</span>
          </div>
        </div>

        <!-- 15-FIELD GRID — semi-transparent so watermark shows through -->
        <div style="
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 22px 16px;
          padding: 16px 18px;
          background: rgba(240, 253, 250, 0.85);
          border: 1.5px solid #0e7c7c;
          border-radius: 10px;
          margin-bottom: 22px;
        ">
          ${fields.map(({ label, value }) => `
            <div>
              <div style="
                font-size: 8.5px;
                font-weight: 700;
                color: #0e7c7c;
                letter-spacing: 0.1em;
                text-transform: uppercase;
                margin-bottom: 2px;
              ">${label}</div>
              <div style="
                font-size: 11px;
                font-weight: 600;
                color: #0f172a;
                line-height: 1.3;
                word-break: break-word;
              ">${value}</div>
            </div>
          `).join('')}
        </div>

        <!-- CRYPTOGRAPHIC ATTESTATION (expanded) -->
        <div style="
          padding: 9px 14px;
          background: #0f1e33;
          border-radius: 8px;
          margin-bottom: 22px;
        ">
          <div style="font-size: 9px; font-weight: 700; color: #7dd3fc; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 4px;">
            🔐 Cryptographic Attestation — Quantum-Safe Signature
          </div>
          <div style="font-size: 9px; color: #cbd5e1; line-height: 1.5;">
            This certificate is signed using <strong style="color: #7dd3fc;">${ledgerRecord.quantum_security?.signature_algorithm || 'ML-DSA-65'}</strong>
            (<strong style="color: #7dd3fc;">${ledgerRecord.quantum_security?.signature_standard || 'NIST FIPS 204'}</strong>) at
            <strong style="color: #7dd3fc;">${ledgerRecord.quantum_security?.protection_level || 'NIST Security Level 3'}</strong>,
            a NIST-standardized post-quantum signature scheme. The underlying assessment record is hashed with
            <strong style="color: #7dd3fc;">${ledgerRecord.quantum_security?.hash_algorithm || 'SHA3-256'}</strong>
            (<strong style="color: #7dd3fc;">${ledgerRecord.quantum_security?.hash_standard || 'NIST FIPS 202'}</strong>) and
            chained onto the Quantum AI Shield Quantum Ledger, making it tamper-evident and verifiable forever.
            Unlike classical RSA/ECDSA signatures—which can be broken by a sufficiently large quantum computer—this signature
            remains secure against both classical and quantum adversaries, satisfying the U.S. NIST 2024 post-quantum
            migration guidance for regulated industries.
          </div>
        </div>

        <!-- VALIDITY & TRUST STATEMENT -->
        <div style="
          padding: 10px 16px;
          background: rgba(255, 255, 255, 0.85);
          border: 1.5px solid #0e7c7c;
          border-radius: 10px;
          margin-bottom: 10px;
        ">
          <div style="font-size: 9.5px; font-weight: 700; color: #0e7c7c; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 5px;">
            📜 Validity &amp; Trust Statement
          </div>
          <div style="font-size: 9.5px; color: #334155; line-height: 1.5;">
            This attestation confirms that <strong style="color: #0f172a;">${ledgerRecord.company || '—'}</strong>
            has completed a structured AI compliance assessment for
            <strong style="color: #0f172a;">${ledgerRecord.ai_system || '—'}</strong>
            against the <strong style="color: #0f172a;">${ledgerRecord.framework || '—'}</strong> framework on
            <strong style="color: #0f172a;">${sealedDate}</strong>.
            The assessment outcome (<strong style="color: ${tier.color};">${tier.label}</strong>)
            is permanently recorded on the Quantum AI Shield Quantum Ledger as Block #${ledgerRecord.block_index ?? '—'}
            and is immutable for the duration of the audit cycle.
          </div>
          <div style="font-size: 9.5px; color: #334155; line-height: 1.5; margin-top: 8px; padding-top: 7px; border-top: 1px dashed #cbd5e1;">
            <strong style="color: #0e7c7c;">Audit Cycle:</strong> Valid from <strong>${sealedDate}</strong> to
            <strong>${nextAuditDate}</strong>. Re-assessment is required at the end of this cycle to maintain status.
            Any tampering of the underlying assessment will invalidate the quantum signature and the certificate will
            fail the verification check at the URL below.
          </div>
          <div style="font-size: 8.5px; color: #64748b; line-height: 1.5; margin-top: 8px; padding-top: 7px; border-top: 1px dashed #cbd5e1; font-style: italic;">
            This is a self-reported compliance record, cryptographically sealed for tamper-evidence. It does not
            constitute a third-party audit, certification, or formal regulatory approval.
          </div>
        </div>

        <!-- FOOTER: text on left, QR (SVG) on right -->
        <div style="display: flex; align-items: flex-end; justify-content: space-between; margin-top: auto; gap: 18px;">
          <div style="flex: 1; font-size: 10px; color: #475569; line-height: 1.55; min-width: 0;">
            <div style="font-weight: 700; color: #0e7c7c; font-size: 11px; margin-bottom: 4px; letter-spacing: 0.1em; text-transform: uppercase;">
              Issued by Quantum AI Shield
            </div>
            <div style="margin-bottom: 2px;">Issued on: <strong>${issuedDate}</strong></div>
            <div style="margin-bottom: 5px;">Verify authenticity by scanning the QR code, or visit:</div>
            <div style="
              font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
              font-size: 9.5px;
              color: #0e7c7c;
              font-weight: 600;
              padding: 5px 9px;
              background: #f0fdfa;
              border-radius: 4px;
              display: inline-block;
              white-space: nowrap;
              letter-spacing: 0;
            ">${verifyUrl}</div>
          </div>
          ${qrSvg ? `
            <div style="text-align: center; flex-shrink: 0;">
              <div style="
                padding: 6px;
                background: white;
                border: 1.5px solid #0e7c7c;
                border-radius: 6px;
                width: 108px;
                height: 108px;
                box-sizing: border-box;
                display: flex;
                align-items: center;
                justify-content: center;
              ">${qrSvg}</div>
              <div style="font-size: 9px; color: #0e7c7c; margin-top: 4px; font-weight: 700; letter-spacing: 0.1em;">SCAN TO VERIFY</div>
            </div>
          ` : ''}
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(container);

  // Wait for logo image to fully load (QR is inline SVG, no wait needed)
  const imgs = Array.from(container.querySelectorAll('img'));
  await Promise.all(imgs.map(async (img) => {
    if (img.decode) {
      try { await img.decode(); } catch (_) {}
    } else if (!img.complete) {
      await new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
    }
  }));
  await new Promise(resolve => setTimeout(resolve, 200));

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: A4_WIDTH_PX,
      height: A4_HEIGHT_PX,
      windowWidth: A4_WIDTH_PX,
      windowHeight: A4_HEIGHT_PX,
    });

    const pdf = new jsPDF('p', 'mm', 'a4');
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, 210, 297, undefined, 'FAST');

    const safeCompany = (ledgerRecord.company || 'company').replace(/[^a-zA-Z0-9]+/g, '_').slice(0, 30);
    pdf.save(`Quantum AIShield_Certificate_${safeCompany}_${ledgerRecord.certificate_id || 'sealed'}.pdf`);
  } catch (err) {
    console.error('PDF generation failed:', err);
    alert('Failed to generate PDF. Please try again.');
  } finally {
    document.body.removeChild(container);
  }
};








  const [showRecord, setShowRecord] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [ledgerError, setLedgerError] = useState('');

  const checkAll = () => {
    const result = {};
    JOURNEY_STEPS.forEach(s => { result[s.key] = s.check(); });
    setCompleted(result);
    setReady(true);
  };

  useEffect(() => {
    checkAll();
    const t1 = setTimeout(checkAll, 300);
    const t2 = setTimeout(checkAll, 1000);
    const t3 = setTimeout(checkAll, 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [user]);

  useEffect(() => { checkAll(); }, [currentPage]);

  useEffect(() => {
    window.addEventListener('focus', checkAll);
    window.addEventListener('qais-journey-update', checkAll);
    return () => {
      window.removeEventListener('focus', checkAll);
      window.removeEventListener('qais-journey-update', checkAll);
    };
  }, []);

  const doneCount  = JOURNEY_STEPS.filter(s => completed[s.key]).length;
  const totalSteps = JOURNEY_STEPS.length;
  const pct        = totalSteps ? Math.round((doneCount / totalSteps) * 100) : 0;
  const allDone    = doneCount === totalSteps && totalSteps > 0;
  const stepsRemaining = totalSteps - doneCount;

// ── Framework-specific compliance tier thresholds ──
  // Calibrated to reflect real-world strictness:
  //   • EU AI Act:    legally binding (Regulation EU 2024/1689), fines up to 7% global turnover
  //   • ISO 42001:    AI management system standard, audit required for certification
  //   • NIST AI RMF:  voluntary risk management framework, flexible by design
  const TIER_THRESHOLDS = {
    eu:   { compliant: 15, partial: 30, baseline: 50 },  // strictest
    iso:  { compliant: 20, partial: 40, baseline: 60 },  // moderate
    nist: { compliant: 30, partial: 50, baseline: 70 },  // most flexible
  };

  const getComplianceTier = (riskScore, framework) => {
    const s = Number(riskScore) || 0;
    const t = TIER_THRESHOLDS[framework] || TIER_THRESHOLDS.iso; // default to ISO if unknown
    if (s <= t.compliant) return { code: 'COMPLIANT',     label: 'Audit-Ready Attested',  icon: '✅', color: '#15803d' };
    if (s <= t.partial)   return { code: 'PARTIAL',       label: 'Partial Compliance',   icon: '🟡', color: '#ca8a04' };
    if (s <= t.baseline)  return { code: 'BASELINE',      label: 'Baseline Assessment',  icon: '🟠', color: '#ea580c' };
    return                       { code: 'NON_COMPLIANT', label: 'Initial Assessment',   icon: '🔴', color: '#dc2626' };
  };


  const handleSaveToLedger = async () => {
    if (saving) return;

    const onboarding  = JSON.parse(localStorage.getItem(userKey('qais_onboarding'))           || '{}');
    const riskResult  = JSON.parse(localStorage.getItem(userKey('qais_risk_result'))           || '{}');
    const euResult    = JSON.parse(localStorage.getItem(userKey('qais_eu_classifier_result'))  || '{}');
    const checkSetup  = JSON.parse(localStorage.getItem(userKey('qais_checklist_setup'))       || '{}');
    const ai          = onboarding.aiSystemProfile || {};

    const fwMap       = { eu: 'EU AI Act', nist: 'NIST AI RMF', iso: 'ISO/IEC 42001' };
    const frameworkStr = fwMap[riskResult.primaryFramework] || 'ISO 42001 / EU AI Act / NIST AI RMF';

    const checkProgress = JSON.parse(localStorage.getItem(userKey('qais_checklist_progress')) || '{}');
    const reqsPassed    = Object.values(checkProgress).filter(Boolean).length;
    const reqsTotal     = checkSetup.checklist?.length || 0;

    const complianceTier = getComplianceTier(riskResult.totalRiskScore, riskResult.primaryFramework);
    const payload = {
      company:              onboarding.companyName     || user?.email || 'Unknown',
      ai_system:            ai.name || onboarding.systemPurpose     || 'AI System',
      framework:            frameworkStr,
      status:               complianceTier.code,
      score:                riskResult.totalRiskScore  || 0,
      risk_level:           (riskResult.riskLevel      || 'UNKNOWN').toUpperCase(),
      requirements_passed:  reqsPassed,
      requirements_total:   reqsTotal,
      sector:               onboarding.industry        || 'Technology',
      early_submission_reason: process.env.NODE_ENV === 'development' ? 'DEV_TESTING_EARLY_SUBMISSION' : undefined,
      public_disclosure:     publicDisclosure === true,  // explicit opt-in only, fails closed
      user_id:               user.id,
      user_email:            user.email,
      quantum_security: {
        signature_algorithm:  'ML-DSA-65',
        signature_standard:   'NIST FIPS 204',
        hash_algorithm:       'SHA3-256',
        hash_standard:        'NIST FIPS 202',
        protection_level:     'NIST Security Level 3',
        quantum_safe_signing: true,
        tamper_evident:       true,
        liboqs_version:       '0.15.0',
        quantum_safe_since:   new Date().toISOString(),
      },
      nist_concern_level: riskResult.nistConcernLevel || null,
      iso_readiness:      riskResult.isoReadiness      || null,
      eu_classification:  euResult.classification       || null,
      eu_applicable:      riskResult.euApplicable       || false,
      deployment_regions: onboarding.deploymentRegions  || null,
    };

 if (!window.confirm(
      `Seal compliance record on the Quantum AI Shield Quantum Ledger?\n\n` +
      `Company: ${payload.company}\n` +
      `Framework: ${payload.framework}\n` +
      `Risk Score: ${payload.score}/100\n` +
      `Status: ${complianceTier.icon} ${complianceTier.label} (${complianceTier.code})\n\n` +
      `⚠️ This action is PERMANENT and IMMUTABLE.\n` +
      `The sealed record cannot be edited or deleted.\n` +
      `The status above will be permanently stamped on the ledger.\n\n` +
      `Next audit due: ${riskResult.primaryFramework === 'nist' ? '90 days' : '365 days'} from today.`
    )) return;

    setSaving(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('No active session. Please log in again.');
      const user = sessionData?.session?.user;
      if (!user) throw new Error('User not found in session');

      // Task #7: capture trial status at seal time -- used to watermark the
      // certificate as "TRIAL SPECIMEN" for records sealed before upgrading.
      const appMetaAtSeal   = user?.app_metadata || {};
      const sealedDuringTrial = !appMetaAtSeal.paid_until;

      const res = await fetch('/api/phase2/record', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 409) {
          const nextDate = new Date(data.next_audit_allowed).toLocaleDateString();
          throw new Error(`Audit cycle not expired. Next seal allowed: ${nextDate}`);
        }
        if (res.status === 403 && data.code === 'SEAL_LIMIT_REACHED') {
          throw new Error(data.error || 'Seal limit reached for your plan.');
        }
        if (res.status === 403 && data.code === 'QLEDGER_NOT_UNLOCKED') {
          throw new Error(data.error || 'QLedger must be purchased before sealing compliance records.');
        }
        throw new Error(data.error || `Server error ${res.status}`);
      }

      const record = {
        company:             payload.company,
        ai_system:           payload.ai_system,
        framework:           payload.framework,
        status:              payload.status,
        score:               payload.score,
        risk_level:          payload.risk_level,
        requirements_passed: payload.requirements_passed,
        requirements_total:  payload.requirements_total,
        sector:              payload.sector,
        certificate_id:      data.certificate_id,
        block_index:         data.block_index,
        block_hash:          data.block_hash,
        sealed_at:           new Date().toISOString(),
        next_audit_date:     data.next_audit_date,
        document_hash:       data.document_hash,
        next_audit_due:      data.next_audit_due,
        quantum_security:    payload.quantum_security,
        nist_concern_level:  payload.nist_concern_level,
        iso_readiness:       payload.iso_readiness,
        eu_classification:   payload.eu_classification,
        // Task #7 additions
        prepared_by:         payload.company,          // self-reported -- never defaults to "Quantum AI Shield"
        public_disclosure:   payload.public_disclosure === true,
        sealed_during_trial: sealedDuringTrial,
      };

      localStorage.setItem(userKey('qais_ledger_record'),    JSON.stringify(record));
      localStorage.setItem(userKey('qais_blockchain_saved'), 'true');
      setLedgerRecord(record);
      setBlockchainSaved(true);
      alert(`🛡️ Sealed on Quantum Ledger!\n\nAttestation ID: ${data.certificate_id}\nBlock: #${data.block_index}`);
    } catch (e) {
      console.error('[handleSaveToLedger]', e.message);
      alert(`❌ Failed to seal record:\n\n${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const fetchLedgerRecord = async () => {
    setLoadingRecord(true);
    setLedgerError('');
    try {
      const raw = localStorage.getItem(userKey('qais_ledger_record'));
      if (raw) { setLedgerRecord(JSON.parse(raw)); }
      else { setLedgerError('No sealed record found for this account.'); }
      setShowRecord(true);
    } catch (e) {
      setLedgerError(e.message);
      setShowRecord(true);
    } finally {
      setLoadingRecord(false);
    }
  };

  if (!ready) return null;

  return (
    <>
      <style>{`
        @keyframes qaisPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.6); }
          50%       { box-shadow: 0 0 0 6px rgba(59,130,246,0); }
        }
        .qais-pulse { animation: qaisPulse 1.5s ease-in-out infinite; }
      `}</style>

      {/* Row 1: Step buttons + progress */}
      <div style={{
        backgroundColor: allDone ? '#0a1628' : '#0f172a',
        borderBottom: allDone ? 'none' : '2px solid #1e293b',
        padding: '6px 20px',
        display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        transition: 'all 0.4s ease'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {allDone && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', boxShadow: '0 0 6px #10b981' }} />}
          <span style={{ fontSize: '12px', color: allDone ? '#10b981' : '#94a3b8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {allDone ? 'Compliance Journey' : 'Journey'}
          </span>
          <span style={{ fontSize: '11px', color: allDone ? '#10b981' : '#06b6d4', fontWeight: '700' }}>{doneCount}/{totalSteps}</span>
        </div>

        <div style={{ flex: 1, display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', minWidth: '0' }}>
          {JOURNEY_STEPS.map((step, i) => {
            const done = !!completed[step.key];
            const isCurrent = currentPage === step.key;
            return (
              <React.Fragment key={step.key}>
                <button onClick={() => onNavigate(step.key)} title={step.label} style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px',
                  backgroundColor: isCurrent ? '#1e40af' : done ? '#065f46' : '#1e293b',
                  border: `1px solid ${isCurrent ? '#3b82f6' : done ? '#10b981' : '#334155'}`,
                  borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s'
                }}>
                  <span style={{ fontSize: '12px' }}>{done ? '✅' : step.icon}</span>
                  <span style={{ fontSize: '11px', color: done ? '#86efac' : isCurrent ? 'white' : '#64748b', fontWeight: done || isCurrent ? '600' : '400' }}>
                    {step.label}
                  </span>
                </button>
                {i < JOURNEY_STEPS.length - 1 && (
                  <div style={{ width: '12px', height: '1px', backgroundColor: done ? '#10b981' : '#334155', flexShrink: 0 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
          <div style={{ width: '70px', height: '4px', backgroundColor: '#1e293b', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', backgroundColor: allDone ? '#10b981' : '#06b6d4', borderRadius: '2px', transition: 'width 0.5s ease' }} />
          </div>
          <span style={{ fontSize: '11px', color: allDone ? '#10b981' : '#06b6d4', fontWeight: '700' }}>{pct}%</span>
        </div>
      </div>

      {/* Row 2: Action buttons */}
      <div style={{
        backgroundColor: allDone ? '#0a1628' : '#0f172a',
        borderBottom: '2px solid #1e293b',
        padding: '5px 20px',
        display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: '12px', color: '#7dd3fc', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
          Actions:
        </span>

        <button
          onClick={() => {
            if (!allDone) return;
            if (window.confirm('Mark your compliance journey as complete?')) {
              localStorage.setItem('qais_journey_done', 'true');
              alert('🎉 Compliance journey marked complete!');
            }
          }}
          disabled={!allDone}
          title={allDone ? 'Confirm all 5 steps are done' : `Complete ${stepsRemaining} more step${stepsRemaining === 1 ? '' : 's'} first`}
          style={{
            padding: '5px 12px', fontSize: '11px', fontWeight: '700',
            backgroundColor: allDone ? '#065f46' : '#1e293b',
            color: allDone ? '#86efac' : '#475569',
            border: `2px solid ${allDone ? '#10b981' : '#334155'}`,
            borderRadius: '8px', cursor: allDone ? 'pointer' : 'not-allowed',
            opacity: allDone ? 1 : 0.55,
            display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
            transition: 'all 0.2s'
          }}
        >
          ✅ All Steps Done
        </button>

        {/* Task #7: public-disclosure opt-in -- relevant every time you seal,
            including sealing an additional company/system after the first. */}
        {allDone && (
          <label
            title="If checked, the public verification link for this record will show company, AI system, framework, status, risk level, and sector. Unchecked (default), only the cryptographic seal validity is shown."
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              fontSize: '11px', color: '#94a3b8', cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={publicDisclosure}
              onChange={(e) => setPublicDisclosure(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Make summary publicly viewable
          </label>
        )}

        {/* Save to Quantum Ledger — no longer permanently disabled once sealed once.
            blockchainSaved now only changes the label/color, never blocks the click:
            sealing additional companies/systems is a real, supported feature, gated
            server-side by checkSealLimits (plan-based ceiling), not by this flag. */}
        <button
          onClick={() => { if (allDone && !saving) handleSaveToLedger(); }}
          disabled={!allDone || saving}
          title={!allDone
            ? `Complete ${stepsRemaining} more step${stepsRemaining === 1 ? '' : 's'} to seal your compliance record`
            : blockchainSaved
              ? 'You already have a sealed record on this account — this seals an additional company/system, subject to your plan\'s seal limit'
              : 'Seal your compliance record on the quantum-safe blockchain ledger'}
          className={allDone && !saving ? 'qais-pulse' : ''}
          style={{
            padding: '5px 12px', fontSize: '11px', fontWeight: '700',
            backgroundColor: !allDone ? '#1e293b' : (blockchainSaved ? '#0c4a6e' : '#1e3a8a'),
            color: !allDone ? '#475569' : 'white',
            border: `2px solid ${!allDone ? '#334155' : (blockchainSaved ? '#0284c7' : '#3b82f6')}`,
            borderRadius: '8px',
            cursor: (!allDone || saving) ? 'not-allowed' : 'pointer',
            opacity: !allDone ? 0.55 : 1,
            display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
            transition: 'all 0.2s'
          }}
        >
          {!allDone
            ? <>🔒 Save to Quantum Ledger ({stepsRemaining} more step{stepsRemaining === 1 ? '' : 's'})</>
            : saving ? <>⏳ Sealing...</>
            : blockchainSaved ? <>🛡️ Seal Another System</>
            : <>🛡️ Save to Quantum Ledger</>}
        </button>

        {blockchainSaved && (
          <button
            onClick={fetchLedgerRecord}
            disabled={loadingRecord}
            title="View your sealed compliance attestation"
            style={{
              padding: '5px 12px', fontSize: '11px', fontWeight: '700',
              backgroundColor: '#0c4a6e', color: '#7dd3fc',
              border: '2px solid #0284c7', borderRadius: '8px',
              cursor: loadingRecord ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
              opacity: loadingRecord ? 0.7 : 1
            }}
          >
            {loadingRecord ? '⏳ Loading...' : '📋 View My Record'}
          </button>
        )}

        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => {
              localStorage.removeItem('qais_blockchain_saved');
              localStorage.removeItem('qais_ledger_record');
              setBlockchainSaved(false);
              setLedgerRecord(null);
              alert('Test mode: Ledger state reset. You can seal again.');
            }}
            style={{
              padding: '5px 12px',
              fontSize: '11px',
              backgroundColor: '#1e293b',
              color: '#facc15',
              border: '2px solid #facc15',
              borderRadius: '8px',
              cursor: 'pointer',
              marginLeft: '8px',
            }}
          >
            🧪 Reset Test Mode
          </button>
        )}
      </div>

      {/* ── Compliance Record Card ── */}
      {showRecord && (
        <div
          id="certificate-container"
          style={{
            backgroundColor: '#0f1e33',
            borderBottom: '2px solid #0284c7',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              maxWidth: '800px',
              width: '100%',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '12px',
            }}
          >
            {/* Watermark image – centered, opacity 0.2 */}
            <img
              src="/logo.png"
              alt="Quantum AI Shield Logo"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                opacity: 0.2,
                pointerEvents: 'none',
                width: '240px',
                height: 'auto',
                zIndex: 0,
              }}
            />

            {/* Certificate content wrapper – sits above watermark */}
            <div style={{ position: 'relative', zIndex: 1, backgroundColor: '#0a1628', borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#7dd3fc', margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  🛡️ Quantum-Sealed Compliance Attestation
                </h3>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button onClick={downloadCertificateAsPDF} style={{ background: '#0c4a6e', border: '1px solid #0284c7', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', color: '#e2e8f0', fontSize: '12px', fontWeight: '500' }}>
                    📄 Download PDF
                  </button>
                  <button
                    onClick={() => setShowAIDecisionsModal(true)}
                    disabled={!blockchainSaved}
                    style={{
                      background: blockchainSaved ? '#0c4a6e' : '#1e293b',
                      border: `1px solid ${blockchainSaved ? '#0284c7' : '#334155'}`,
                      borderRadius: '6px',
                      padding: '6px 12px',
                      cursor: blockchainSaved ? 'pointer' : 'not-allowed',
                      color: blockchainSaved ? '#e2e8f0' : '#64748b',
                      fontSize: '12px',
                      fontWeight: '500',
                    }}
                  >
                    🤖 AI Decisions
                  </button>
                  <button onClick={() => setShowRecord(false)} style={{ background: 'none', border: 'none', color: '#e2e8f0', cursor: 'pointer', fontSize: '18px' }}>✕</button>
                </div>
              </div>

              <p style={{ fontSize: '10.5px', color: '#64748b', fontStyle: 'italic', margin: '0 0 14px', lineHeight: 1.5 }}>
                This is a self-reported compliance record, cryptographically sealed for tamper-evidence.
                It does not constitute a third-party audit, certification, or formal regulatory approval.
              </p>

              {ledgerRecord?.sealed_during_trial && (
                <div style={{
                  display: 'inline-block', marginBottom: '14px', padding: '4px 10px',
                  backgroundColor: 'rgba(220,38,38,0.12)', border: '1px solid #dc2626',
                  borderRadius: '6px', color: '#fca5a5', fontSize: '11px', fontWeight: '700',
                  letterSpacing: '0.05em',
                }}>
                  ⚠️ TRIAL SPECIMEN — sealed during trial period
                </div>
              )}

              <div id="certificate-content">
                {ledgerError ? (
                  <div style={{ backgroundColor: '#1e0a0a', border: '1px solid #7f1d1d', borderRadius: '8px', padding: '12px 16px' }}>
                    <p style={{ fontSize: '12px', color: '#fca5a5', margin: 0 }}>⚠️ {ledgerError}</p>
                  </div>
                ) : ledgerRecord ? (
                  <>
                    {/* 3‑column grid for main certificate fields */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
                      {[
                        { label: 'COMPANY', value: ledgerRecord.company, icon: '🏢' },
                        { label: 'AI SYSTEM', value: ledgerRecord.ai_system, icon: '🤖' },
                        { label: 'FRAMEWORK', value: ledgerRecord.framework, icon: '📋' },

                        { label: 'STATUS', value: ledgerRecord.status, icon:
                          ledgerRecord.status === 'COMPLIANT' ? '✅' :
                          ledgerRecord.status === 'PARTIAL'   ? '🟡' :
                          ledgerRecord.status === 'BASELINE'  ? '🟠' :
                          ledgerRecord.status === 'NON_COMPLIANT' ? '🔴' : '🔄'
                        },
                        { label: 'SCORE', value: `${ledgerRecord.score}/100`, icon: '📊' },
                        { label: 'RISK LEVEL', value: ledgerRecord.risk_level, icon: '⚖️' },
                        { label: 'REQUIREMENTS', value: `${ledgerRecord.requirements_passed}/${ledgerRecord.requirements_total}`, icon: '☑️' },
                        { label: 'SECTOR', value: ledgerRecord.sector, icon: '🏭' },
                        { label: 'ATTESTATION ID', value: ledgerRecord.certificate_id, icon: '🔖' },
                        { label: 'BLOCK #', value: ledgerRecord.block_index != null ? `#${ledgerRecord.block_index}` : '—', icon: '⛓️' },
                        { label: 'SEALED AT', value: ledgerRecord.sealed_at ? new Date(ledgerRecord.sealed_at).toLocaleString() : '—', icon: '📅' },
                        { label: 'QUANTUM ALGORITHM', value: ledgerRecord.quantum_security?.signature_algorithm ? `${ledgerRecord.quantum_security.signature_algorithm} (${ledgerRecord.quantum_security.signature_standard})` : '—', icon: '🔐' },
                        { label: 'DOCUMENT HASH', value: ledgerRecord.document_hash ? ledgerRecord.document_hash.slice(0,16) + '…' : '—', icon: '🔗' },
                        { label: 'PREPARED BY', value: ledgerRecord.prepared_by || ledgerRecord.company || '—', icon: '🛡️' },
                        { label: 'NEXT AUDIT DUE', value: (() => {
                          if (!ledgerRecord.next_audit_date) return '—';
                          const dueDate = new Date(ledgerRecord.next_audit_date);
                          const today = new Date();
                          const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                          const daysText = diffDays > 0 ? ` (${diffDays} day${diffDays !== 1 ? 's' : ''} left)` : ' (overdue)';
                          return dueDate.toLocaleDateString() + daysText;
                        })(), icon: '🔁' },
                      ].map(({ label, value, icon }) => (
                        <div key={label} style={{ backgroundColor: '#0c1a2a', border: '1px solid #1e3a5f', borderRadius: '8px', padding: '10px 12px' }}>
                          <p style={{ fontSize: '13px', color: '#7dd3fc', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{icon} {label}</p>
                          <p style={{ fontSize: '13px', fontWeight: '700', color: '#e2e8f0', margin: 0, wordBreak: 'break-word' }}>{value || '—'}</p>
                        </div>
                      ))}
                    </div>

                    {/* 3‑column grid for quantum‑safe cryptography */}
                    {ledgerRecord.quantum_security && (
                      <div style={{ backgroundColor: '#0a1a2e', border: '1px solid #1e3a5f', borderRadius: '8px', padding: '16px' }}>
                        <p style={{ fontSize: '12px', color: '#60a5fa', margin: '0 0 12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          🔐 Quantum‑Safe Cryptography
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                          {Object.entries(ledgerRecord.quantum_security).map(([key, value]) => {
                            let label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            let displayValue = value;
                            if (typeof value === 'boolean') displayValue = value ? 'Yes' : 'No';
                            if (key === 'quantum_safe_since' && value) displayValue = new Date(value).toLocaleString();
                            if (key === 'liboqs_version') displayValue = value;
                            if (key === 'hash_algorithm') label = 'Hash Algorithm';
                            if (key === 'hash_standard') label = 'Hash Standard';
                            if (key === 'signature_algorithm') label = 'Signature Algorithm';
                            if (key === 'signature_standard') label = 'Signature Standard';
                            if (key === 'protection_level') label = 'Protection Level';
                            return (
                              <div key={key} style={{ borderBottom: '1px solid #1e3a5f', paddingBottom: '6px' }}>
                                <div style={{ fontSize: '13px', color: '#7dd3fc', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: '500', wordBreak: 'break-word' }}>{displayValue}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{ fontSize: '12px', color: '#475569', margin: 0 }}>No record found for {user?.email}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Decisions Modal */}
      {showAIDecisionsModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          }}
          onClick={() => setShowAIDecisionsModal(false)}
        >
          <div
            style={{
              backgroundColor: '#0f172a', borderRadius: '16px',
              maxWidth: '1200px', width: '100%', maxHeight: '90vh',
              overflow: 'auto', padding: '20px', position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '20px', color: '#e2e8f0', margin: 0 }}>🤖 AI Decision Audit Trail</h3>
              <button onClick={() => setShowAIDecisionsModal(false)} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '24px', cursor: 'pointer' }}>✕</button>
            </div>
            <AIDecisionsTab
              currentUserEmail={user?.email}
              initialData={prefetchedDecisions}
              initialLoading={prefetchedLoading}
            />
          </div>
        </div>
      )}
    </>
  );
}

// ── Onboarding Tooltip ──
export function OnboardingTooltip({ page }) {
  const [dismissed, setDismissed] = useState(true);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(TOOLTIP_KEY) || '[]');
      const isDismissed = saved.includes(page);
      setDismissed(isDismissed);
      if (!isDismissed) {
        const timer = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(timer);
      }
    } catch {}
  }, [page]);

  const dismiss = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(TOOLTIP_KEY) || '[]');
      if (!saved.includes(page)) {
        localStorage.setItem(TOOLTIP_KEY, JSON.stringify([...saved, page]));
      }
    } catch {}
    setVisible(false);
    setDismissed(true);
  };

  const dismissAll = () => {
    try { localStorage.setItem(TOOLTIP_KEY, JSON.stringify(Object.keys(TOOLTIPS))); } catch {}
    setVisible(false);
    setDismissed(true);
  };

  const tip = TOOLTIPS[page];
  if (!tip || dismissed || !visible) return null;

  return (
    <div style={{
      position: 'fixed', bottom: '24px', right: '24px', zIndex: 1000,
      backgroundColor: '#0f172a', border: '2px solid #3b82f6', borderRadius: '14px',
      padding: '16px 18px', maxWidth: '300px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      animation: 'slideUp 0.3s ease',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <p style={{ fontSize: '14px', fontWeight: 'bold', color: 'white', margin: 0 }}>{tip.title}</p>
        <button onClick={dismiss} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '16px', padding: '0 0 0 8px', lineHeight: 1 }}>×</button>
      </div>
      <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 12px', lineHeight: '1.6' }}>{tip.text}</p>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={dismiss} style={{ flex: 1, padding: '6px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
          Got it
        </button>
        <button onClick={dismissAll} style={{ padding: '6px 10px', backgroundColor: 'transparent', color: '#475569', border: '1px solid #334155', borderRadius: '8px', cursor: 'pointer', fontSize: '11px' }}>
          Don&apos;t show again
        </button>
      </div>
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}